import { z } from 'zod';

/**
 * Where a proposed action stands (IMM-436.5).
 *
 * `PENDING` is the only non-terminal state; everything else is final and every
 * transition compare-and-sets on it, so approve / reject / expire / void can
 * race and still yield exactly one execution.
 *
 * `EXPIRED` counts as rejected everywhere — the member's silence over the
 * approval window is an answer. `VOID` is what a revoked grant leaves behind:
 * the action can no longer be approved because the authority it would have run
 * under is gone.
 */
export const PROPOSED_ACTION_STATUSES = ['PENDING', 'APPROVED', 'REJECTED', 'EXPIRED', 'VOID'] as const;

export const ProposedActionStatusSchema = z.enum(PROPOSED_ACTION_STATUSES);

export type ProposedActionStatus = (typeof PROPOSED_ACTION_STATUSES)[number];

/** One row in the member's pending-approvals list. */
export const PendingApprovalSchema = z.object({
  id: z.uuid(),
  skillInstanceId: z.uuid(),
  skillName: z.string().min(1),
  /** One human-readable line: what the skill wants to do. */
  summary: z.string().min(1),
  expiresAt: z.iso.datetime(),
  createdAt: z.iso.datetime(),
});

export type PendingApproval = z.infer<typeof PendingApprovalSchema>;

export const ListPendingApprovalsResponseSchema = z.object({
  data: z.array(PendingApprovalSchema),
});

export type ListPendingApprovalsResponse = z.infer<typeof ListPendingApprovalsResponseSchema>;

/**
 * The full proposal, including the frozen parameters the member is actually
 * agreeing to. Showing the recorded call verbatim is the point of the screen —
 * an approval against a paraphrase would not be an informed one.
 */
export const ProposedActionDetailSchema = PendingApprovalSchema.extend({
  status: ProposedActionStatusSchema,
  toolId: z.string().min(1),
  /** Nullable for the same reason a tool call's are: IMM-436.9 redacts them after 90 days. */
  parameters: z.record(z.string(), z.unknown()).nullable(),
  skillRunId: z.uuid(),
  decidedAt: z.iso.datetime().nullable(),
  /** Present once approved and executed — what actually happened when it ran. */
  executionOutcome: z.enum(['OK', 'ERROR']).nullable(),
});

export type ProposedActionDetail = z.infer<typeof ProposedActionDetailSchema>;

export const ProposedActionPathParamsSchema = z.object({
  workspaceId: z.uuid(),
  id: z.uuid(),
});
